import {
  Box,
  Button,
  LinkBehavior,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  useTheme,
} from "@crayon/design-system-react";
import TableBodyCell from "components/primitives/TableBodyCell";
import TableBodySkeleton from "components/primitives/TableBodySkeleton";
import TableHeaderCell from "components/primitives/TableHeaderCell";
import { useSyncOverviewContext } from "context/syncOverviewContext";
import useSyncHistoryPaged from "hooks/api/useSyncHistoryPaged";
import useSyncTypeGuard from "hooks/useSyncTypeGuard";
import { useMemo, useState } from "react";
import AppRoutes from "routes/app-routes";
import DefaultPaginationOptions from "types/defaultPaginationOptions";
import { SyncDigestStatsViewModel } from "types/sync-digest-stats-vm";

const SyncHistoryTable = () => {
  const { source, target, program, syncType } = useSyncOverviewContext();

  useSyncTypeGuard(syncType);

  const [page, setPage] = useState(0);
  const rowsPerPageOptions = useMemo<number[]>(() => DefaultPaginationOptions, []);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[0]);
  const [syncHistoryItemsCount, setSyncHistoryItemsCount] = useState(0);

  const { syncHistory, isSyncHistoryFetching } = useSyncHistoryPaged(
    source,
    target,
    program,
    syncType,
    page * rowsPerPage, // 'page' is zero-based
    rowsPerPage,
  );

  const theme = useTheme();

  const syncHistoryItems = useMemo<SyncDigestStatsViewModel[]>(() => {
    if (syncHistory?.paging.count !== undefined) setSyncHistoryItemsCount(syncHistory.paging.count);

    return syncHistory?.items ?? [];
  }, [syncHistory]);

  const columnOptions = useMemo<string[]>(
    () =>
      syncType === "Product"
        ? ["When", "Status", "Products Synced", "Error Count", "Warning Count"]
        : ["When", "Status", "Customers Synced", "Subscriptions Synced", "Customers Failed"],
    [syncType],
  );

  const renderHeaderCell = (data: string, i: number) => (
    <TableHeaderCell
      key={data + String(i)}
      colSpan={i === columnOptions.length - 1 ? 2 : 1}
      columnName={data}
    />
  );

  const onRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const renderStatusCell = (digest: SyncDigestStatsViewModel) => {
    let label: string;
    let color: string;

    if (syncType === "Billing") {
      if (digest.errorCount > 0) {
        label = "Failure";
        color = theme.palette.error.main;
      } else if (digest.secondarySyncCount === 0) {
        label = "No Change";
        color = "inherit";
      } else {
        label = "Success";
        color = theme.palette.success.main;
      }
    }
    // Product history case
    else if (digest.errorCount > 0) {
      label = "Failure";
      color = theme.palette.error.main;
    } else if (digest.warningCount > 0) {
      label = "Warning";
      color = theme.palette.warning.main;
    } else {
      label = "Success";
      color = theme.palette.success.main;
    }

    return <TableBodyCell label={label} color={color} />;
  };
  // prettier-ignore
  const getHistoryDetailsRoute = (digestId: string) => syncType === "Billing"
    ? AppRoutes.billingSyncHistoryDetails.buildRoute(source, target, program, digestId)
    : AppRoutes.productSyncHistoryDetails.buildRoute(source, target, program, digestId);

  const DataTableBody = (
    <TableBody>
      {syncHistoryItems.map((x) => (
        <TableRow key={x.digestId}>
          <TableBodyCell label={x.when.extToDateTimeString()} />
          {renderStatusCell(x)}
          <TableBodyCell label={`${x.syncCount}`} />
          <TableBodyCell
            label={syncType === "Billing" ? `${x.secondarySyncCount}` : `${x.errorCount}`}
          />
          <TableBodyCell label={syncType === "Billing" ? `${x.errorCount}` : `${x.warningCount}`} />
          <TableCell align="right">
            <Button component={LinkBehavior} href={getHistoryDetailsRoute(x.digestId)}>
              View
            </Button>
          </TableCell>
        </TableRow>
      ))}
    </TableBody>
  );

  return (
    <TableContainer component={Paper}>
      <Table testId="Sync History">
        <TableHead>
          <TableRow>{columnOptions.map(renderHeaderCell)}</TableRow>
        </TableHead>
        {isSyncHistoryFetching ? (
          <TableBodySkeleton colsCount={columnOptions.length} rowsCount={3} />
        ) : (
          DataTableBody
        )}
      </Table>
      <TablePagination
        component={Box}
        count={syncHistoryItemsCount}
        page={page}
        onPageChange={(_: React.MouseEvent<HTMLButtonElement>, newPage: number) => setPage(newPage)}
        rowsPerPageOptions={rowsPerPageOptions}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={onRowsPerPageChange}
        sx={{ my: 1 }}
      />
    </TableContainer>
  );
};

export default SyncHistoryTable;
